<script setup>
/* eslint semi: 1 */
import { inject, ref } from 'vue';
import { DateTime } from 'luxon';
import { FwbButton, FwbCheckbox, FwbDropdown, FwbRadio } from 'flowbite-vue';
import { useMediaFiltering } from '@/composables/media/use-media-filtering';

const $currentUser = inject('$currentUser');

const {
  schema,
  uiSchema,
  hasCommittedFilters,
  committedFiltersCount,
  setGroupValue,
  toggleMultiSelectOption,
  toggleAllForGroup,
  initialize,
  apply,
  clearAll
} = useMediaFiltering($currentUser);

const emit = defineEmits(['apply', 'clear']);

const expandedGroups = ref(new Set(schema.value.map(g => g.key)));

function toggleGroup (key) {
  const next = new Set(expandedGroups.value);
  next.has(key) ? next.delete(key) : next.add(key);
  expandedGroups.value = next;
}

function toggleMultiSelectOptionItem (groupKey, optionValue) {
  if (optionValue === 'all') {
    toggleAllForGroup(groupKey);
  } else {
    toggleMultiSelectOption(groupKey, optionValue);
  }
}

function onDateRangeChange (groupKey, data) {
  if (Array.isArray(data) && data.length === 2) {
    const fromDt = DateTime.fromJSDate(new Date(data[0]));
    const toDt = DateTime.fromJSDate(new Date(data[1]));
    if (fromDt.isValid && toDt.isValid) {
      setGroupValue(groupKey, { from: fromDt.toISODate(), to: toDt.toISODate() });
      return;
    }
  }

  setGroupValue(groupKey, { from: null, to: null });
}

function onApply () {
  apply();
  emit('apply');
  closeDropdown();
}

function onClearAll () {
  clearAll();
  emit('clear');
  closeDropdown();
}

function closeDropdown () {
  /**
   * Simulates a click outside the dropdown to trigger its
   * close behavior. This is needed because the dropdown
   * component does not provide a way to close it and uses
   * a click outside listener.
   */
  setTimeout(() => {
    document.dispatchEvent(new MouseEvent('click', { bubbles: true }));
  });
}
</script>
<template>
  <FwbDropdown
    ref="dropdownRef"
    placement="bottom"
    class="cursor-pointer group z-[11]"
    content-wrapper-class="media-filter-content bottom-[unset] top-12 h-fit bg-white shadow-2xl rounded border-gray-200 border"
    :close-inside="false"
    data-component-element-id="media-filter-dropdown"
    @show="initialize"
  >
    <template #trigger>
      <FwbButton
        class="justify-center px-2 font-medium"
        :class="{
          'text-gray-900': !hasCommittedFilters,
          'text-primary-700': hasCommittedFilters
        }"
        data-component-element-id="media-filter-actions-btn"
        color="light"
      >
        <template #prefix>
          <BaseIconSvg
            name="flowbite:filter-solid"
            class="w-4! h-4!"
          />
        </template>

        <span>{{ $t('label.filters') }}</span>
        <span
          v-if="hasCommittedFilters"
          class="ml-1 inline-flex items-center justify-center"
          data-component-element-id="media-filter-actions-btn-filters-count"
        >
          ({{ committedFiltersCount }})
        </span>

        <template #suffix>
          <BaseIconSvg
            name="flowbite:angle-down-outline"
            class="w-4! h-4! group-[&.fwb-dropdown-active]:-rotate-180 transition-transform duration-200 text-gray-900!"
          />
        </template>
      </FwbButton>
    </template>

    <div class="w-72 flex flex-col p-3 gap-3">
      <!-- Panel header -->
      <div class="flex items-center justify-between text-sm font-medium">
        <span class="text-gray-900">{{ $t('label.filters') }}</span>
        <button
          type="button"
          class="h-7 flex items-center bg-transparent border-0! text-primary-700 hover:text-primary-900"
          data-component-element-id="media-filter-clear-btn"
          :aria-label="$t('buttons.clearAll')"
          @click="onClearAll"
        >
          {{ $t('buttons.clearAll') }}
        </button>
      </div>

      <!-- Filter groups (dynamic) -->
      <div class="flex flex-col w-full">
        <div
          v-for="group in uiSchema"
          :key="group.key"
          class="flex flex-col"
          :data-component-element-id="`media-filter-group-${group.key}`"
        >
          <!-- Group header / toggle -->
          <button
            type="button"
            class="flex items-center justify-between gap-3 py-3 text-sm border-gray-100 border-t-0 border-x-0 border-b w-full text-left cursor-pointer bg-transparent"
            :class="expandedGroups.has(group.key) ? 'text-gray-900' : 'text-gray-500'"
            :data-component-element-id="`media-filter-group-${group.key}-expand-btn`"
            @click="toggleGroup(group.key)"
          >
            <span class="font-medium">
              {{ $t(group.i18n) }}
              <span
                v-if="group.activeFiltersCount > 0"
                class="ml-1 inline-flex items-center justify-center text-primary-700"
              >
                ({{ group.activeFiltersCount }})
              </span>
            </span>

            <BaseIconSvg
              name="flowbite:angle-down-outline"
              class="w-4! h-4! transition-transform duration-200 shrink-0"
              :class="{ '-rotate-180': expandedGroups.has(group.key) }"
            />
          </button>

          <!-- Group body -->
          <div
            v-show="expandedGroups.has(group.key)"
            class="border-gray-100 border-t-0 border-x-0 border-b"
            :data-component-element-id="`media-filter-group-${group.key}-body`"
          >
            <!-- multi-select: chip toggles -->
            <template v-if="group.type === 'multi-select'">
              <div class="flex flex-col gap-2 py-2">
                <FwbCheckbox
                  value="all"
                  :label="$t('label.all')"
                  :model-value="group.isAllSelected"
                  :data-component-element-id="`media-filter-group-${group.key}-option-all`"
                  @update:model-value="toggleMultiSelectOptionItem(group.key, 'all')"
                />

                <FwbCheckbox
                  v-for="option in group.options"
                  :key="option.value"
                  :label="$t(option.i18n)"
                  :model-value="group.effectiveValue.includes(option.value)"
                  :data-component-element-id="`media-filter-group-${group.key}-option-${option.value}`"
                  @update:model-value="toggleMultiSelectOption(group.key, option.value)"
                />
              </div>
            </template>

            <!-- single-select: radio list -->
            <template v-else-if="group.type === 'single-select'">
              <div class="flex flex-col py-1">
                <FwbRadio
                  v-for="option in group.options"
                  class="cursor-pointer [&>input]:focus:ring-transparent! text-start"
                  :model-value="group.value"
                  :value="option.value"
                  :key="option.value"
                  :label="$t(option.i18n)"
                  :data-component-element-id="`media-filter-group-${group.key}-option-${option.value}`"
                  @update:model-value="setGroupValue(group.key, option.value)"
                />
              </div>
            </template>

            <!-- date-range -->
            <template v-else-if="group.type === 'date-range'">
              <div class="py-3 flex items-center relative">
                <el-date-picker
                  :model-value="group.value ? [group.value.from, group.value.to] : null"
                  class="cstm-datepicker-grey"
                  type="daterange"
                  :start-placeholder="$t('label.from')"
                  :end-placeholder="$t('label.to')"
                  :teleported="false"
                  :clearable="false"
                  :data-component-element-id="`media-filter-group-${group.key}-date-range`"
                  @update:model-value="onDateRangeChange(group.key, $event)"
                />
                <div
                  class="absolute right-[5px] cursor-pointer flex opacity-[0.3] hover:opacity-100"
                  @click="onDateRangeChange(group.key, null)"
                >
                  <el-icon><CircleClose /></el-icon>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Footer: apply -->
      <div class="flex items-center justify-end">
        <FwbButton
          class="justify-center border-none"
          size="sm"
          color="blue"
          data-component-element-id="media-filter-apply-btn"
          @click="onApply"
        >
          {{  $t('buttons.apply') }}
        </FwbButton>
      </div>
    </div>
  </FwbDropdown>
</template>

<style lang="scss" scoped>
@reference '#tailwind.scss';

:deep(.media-filter-content) {
  .el-date-editor.el-input, .el-date-editor.el-input__wrapper {
    @apply w-[unset] rounded-md!;
  }

  .el-range-input {
    @apply text-sm!;
  }
}
</style>
